<template>
  <div class="avatar-gallery">
    <avatar
      v-for="(avatar, index) in avatarsToDisplay"
      :key="index"
      :person="avatar"
      :size="size"
      :class="{'avatar-gallery__avatar-tight': stackTightly}"
      :show-name-on-hover="!suppressAvatarTooltip"
      class="avatar-gallery__avatar"
    />
    <span 
      v-if="hasRemainingAvatars && suppressRemainingPeopleTooltip" 
      class="avatar-gallery__remain">{{ `+${remainingAvatars}` }}
    </span>
    <el-tooltip
      v-if="hasRemainingAvatars && !suppressRemainingPeopleTooltip"
      placement="bottom"
    >
      <template #content>
        <div>
          <div v-for="(avatar, index) in listRemainingAvatars" :key="index">
            {{ avatar.name }}
            <br>
          </div>
          {{remainingTooltipAvatarsNote}}
        </div>
      </template>
      <span class="avatar-gallery__remain">{{ `+${remainingAvatars}` }}</span>
    </el-tooltip>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import PersonAvatar from '@/components/global/avatar.vue'

/* Helpers */

/* Data types */
import { IPersonBase } from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'AvatarGallery',

  components: {
    PersonAvatar,
  },

  props: {
    people: {
      type: Array as () => IPersonBase[],
      required: true,
    },
    /* If true, avatars will stack with only 1px separation between each other horizontally */
    stackTightly: {
      type: Boolean,
      default: false,
    },
    maxNumAvatars: {
      type: Number,
      default: 4,
    },
    maxNumAvatarsTooltip: {
      type: Number,
      default: 12,
    },
    size: {
      type: Number,
      default: 25,
    },
    suppressAvatarTooltip: {
      type: Boolean,
      default: false,
    },
    suppressRemainingPeopleTooltip: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    hasRemainingAvatars(): boolean {
      return this.remainingAvatars > 0
    },
    avatarsToDisplay(): IPersonBase[] {
      const arrayToDisplay = [...this.people]
      return arrayToDisplay.splice(0, this.maxNumAvatars)
    },
    remainingAvatars(): number {
      return this.people.length - this.maxNumAvatars
    },
    listRemainingAvatars(): IPersonBase[] {
      const arrayToDisplay = [...this.people].splice(this.maxNumAvatars)
      return arrayToDisplay.splice(0, this.maxNumAvatarsTooltip)
    },
    remainingTooltipAvatarsNote(): string {
      const remaining =
        [...this.people].splice(this.maxNumAvatars).length -
        this.maxNumAvatarsTooltip

      if (remaining > 0) {
        return '...and ' + remaining + ' more'
      }

      return ''
    },
  },
})
</script>

<style lang="scss">
.avatar-gallery {
  display: flex;
  align-items: center;

  &__avatar {
    /* !important to override margin-right on first avatar of gallery for ifp-filter-jobs dropdown */
    margin-right: 0 !important;
    margin-left: 6px;

    &-tight {
      margin-left: 1px;
    }

    &:first-of-type {
      margin-left: 0;
    }
  }

  &__remain {
    padding-left: 4px;
    @include lettering--p-sm;
    color: var(--color-text-third);
    cursor: default;

    @include media-breakpoint-up(sm) {
      margin-right: 0;
    }
  }
}
</style>
